import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Workspace } from '@workspaces/models/workspaces';
import { WorkspaceService } from '@workspaces/services/workspace.service';
import { workspaceListFeature, WorkspaceFetch } from '@workspaces/store/workspace-list.feature';
import { EditComponent } from '@workspaces/utils/edit-component';
import { Subscription } from 'rxjs';
import { AppState } from 'src/app/state/app.state';
import { ConfirmationModalComponent } from 'src/app/confirmation-modal/confirmation-modal.component';
import { WorkspaceStatusClassPipe } from '@workspaces/utils/pipes/workspace-status-class.pipe';
import { WorkspaceStatusPipe } from '@workspaces/utils/pipes/workspace-status.pipe';
import { RequiredRoleDirective } from '@workspaces/utils/workspace-guard.directive';
import { MatProgressBar } from '@angular/material/progress-bar';

@Component({
  selector: 'app-status-management',
  templateUrl: './status-management.component.html',
  styleUrls: ['./status-management.component.scss', '../../global-workspace-styles.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    WorkspaceStatusClassPipe,
    WorkspaceStatusPipe,
    RequiredRoleDirective,
    MatProgressBar
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StatusManagementComponent extends EditComponent implements OnInit, OnDestroy {
  private readonly sub = new Subscription();
  public currentWorkspace: Workspace | null = null;
  public isLoading = false;
  public errorMessage: string | null = null;
  public successMessage: string | null = null;
  constructor(
    protected override readonly route: ActivatedRoute,
    protected readonly workspaceService: WorkspaceService,
    protected override readonly snackbar: MatSnackBar,
    protected override readonly router: Router,
    private readonly store: Store<AppState>,
    public readonly dialog: MatDialog,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {
    super(route, snackbar, router);
  }

  public override ngOnInit() {
    super.ngOnInit();
    if (this.workspaceId) {
      this.dispatchFetches(this.workspaceId);
      this.sub.add(
        this.store.select(workspaceListFeature.workspaceById(this.workspaceId)).subscribe(workspace => {
          if (workspace && this.workspaceId === workspace.id) {
            this.currentWorkspace = workspace;

            this.changeDetectorRef.markForCheck(); // Dont copy the state, just mark for check to rerender
          }
        })
      );
    }
  }

  private dispatchFetches(workspaceId: string): void {
    this.store.dispatch(WorkspaceFetch({ workspaceId }));
  }

  public changeStatus(toArchive: boolean): void {
    if (!this.workspaceId || this.isLoading) {
      return;
    }
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      maxWidth: '400px',
      data: {
        message: `Do you want to ${toArchive ? 'archive' : 'unarchive'} this workspace?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }
      this.isLoading = true;
      if (toArchive) {
        this.sub.add(
          this.workspaceService
            .archiveWorkspace(this.workspaceId)
            .subscribe(() => {
              this.snackbar.open('Moved to archive.', 'Dismiss', {
                duration: 5000,
                panelClass: ['success-snackbar']
              });
            })
            .add(() => {
              this.isLoading = false;
              this.dispatchFetches(this.workspaceId);
            })
        );
      } else {
        this.sub.add(
          this.workspaceService
            .unarchiveWorkspace(this.workspaceId)
            .subscribe(() => {
              this.snackbar.open('Moved to unarchive.', 'Dismiss', {
                duration: 5000,
                panelClass: ['success-snackbar']
              });
            })
            .add(() => {
              this.isLoading = false;
              this.dispatchFetches(this.workspaceId);
            })
        );
      }
    });
  }

  public handleDelete(): void {
    if (!this.workspaceId || this.currentWorkspace?.is_deleted || this.isLoading) {
      return;
    }
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      maxWidth: '400px',
      data: {
        message: `Do you want to delete this workspace? This step is not reversible!`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }
      this.isLoading = true;

      this.sub.add(
        this.workspaceService
          .deleteWorkspace(this.workspaceId)
          .subscribe(() => {
            this.snackbar.open('Workspace was deleted!', 'Dismiss', {
              duration: 5000,
              panelClass: ['success-snackbar']
            });
            this.router.navigate(['/workspaces']);
          })
          .add(() => {
            this.isLoading = false;
          })
      );
    });
  }

  public moveToDrafting() {
    this.isLoading = true;
    this.sub.add(
      this.workspaceService
        .withdrawToDrafting(this.workspaceId)
        .subscribe(() => {
          this.snackbar.open('Workspace status updated!', 'Dismiss', {
            duration: 5000,
            panelClass: ['success-snackbar']
          });
        })
        .add(() => {
          this.isLoading = false;
          this.dispatchFetches(this.workspaceId);
        })
    );
  }

  public moveToReview() {
    this.isLoading = true;
    this.sub.add(
      this.workspaceService
        .submitForReview(this.workspaceId)
        .subscribe(() => {
          this.snackbar.open('Workspace status updated!', 'Dismiss', {
            duration: 5000,
            panelClass: ['success-snackbar']
          });
        })
        .add(() => {
          this.isLoading = false;
          this.dispatchFetches(this.workspaceId);
        })
    );
  }

  public moveToApprove() {
    this.isLoading = true;
    this.sub.add(
      this.workspaceService
        .approveWorkspace(this.workspaceId)
        .subscribe(() => {
          this.snackbar.open('Workspace status updated!', 'Dismiss', {
            duration: 5000,
            panelClass: ['success-snackbar']
          });
        })
        .add(() => {
          this.isLoading = false;
          this.dispatchFetches(this.workspaceId);
        })
    );
  }

  public moveToFinish() {
    this.isLoading = true;
    this.sub.add(
      this.workspaceService
        .finalizeWorkspace(this.workspaceId)
        .subscribe(() => {
          this.snackbar.open('Workspace status updated!', 'Dismiss', {
            duration: 5000,
            panelClass: ['success-snackbar']
          });
        })
        .add(() => {
          this.isLoading = false;
          this.dispatchFetches(this.workspaceId);
        })
    );
  }

  public ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
